import { useState } from 'react';

import _ from 'lodash';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Tooltip } from 'react-tooltip';
import { Button } from 'ui-kit';

import { gql } from '@apollo/client';

import { AssetClass, FileState, LoanField, ProductType } from '__generated__/globalTypes';

import FieldsList from './FieldsList';
import FileHeadersList from './FileHeadersList';
import LLMAutoMapComponent from './LLMAutoMap';
import { useMapHeadersToFields } from '../hooks/useMapHeadersToFields';
import { FIELD_CONFIG } from '../loanFields';
import { MapFieldsOperationWrapper } from '../MapFieldsOperation';
import { MappingCardFile } from './__generated__/MappingCardFile';

const MappingCardFragments = {
  loanTape: gql`
    fragment MappingCardFile on File {
      id
      createdDate: created_date
      name
      assetClass: asset_class
      headers
      field_mapping {
        file_column
        field
      }
      productType: product_type
      state
      processing_percent
    }
  `,
};

export interface FieldIdAndLabel {
  fieldId: LoanField;
  label: string;
  fallback?: LoanField;
}

const convertToFieldIdAndLabel = (fc: {
  field: LoanField;
  label: string;
  fallback?: LoanField;
}): FieldIdAndLabel => {
  return { fieldId: fc.field, label: fc.label, fallback: fc.fallback };
};

export type FieldsMap = {
  [fieldId in LoanField]: string;
};

const mappingArrayToMap = (mapping: { field: LoanField; file_column: string }[]) => {
  const map: FieldsMap = {} as FieldsMap;
  mapping.forEach((m) => {
    map[m.field] = m.file_column;
  });
  return map;
};

const getFileFieldReqs = (selectedFile: MappingCardFile) => {
  const assetClass = selectedFile.assetClass;

  const armedAssetClass =
    assetClass === AssetClass.HOME && selectedFile.productType === 'ARM' ? 'HOME_ARM' : assetClass;

  const requiredFields = FIELD_CONFIG[armedAssetClass].requiredFields.map(convertToFieldIdAndLabel);
  const optionalFields =
    FIELD_CONFIG[armedAssetClass].optionalLoanFields.map(convertToFieldIdAndLabel);
  return {
    requiredFields,
    optionalFields,
  };
};

type Props = {
  selectedFile: MappingCardFile;
};

const MapRequiredFieldsCard = ({ selectedFile }: Props): JSX.Element => {
  const { requiredFields, optionalFields } = getFileFieldReqs(selectedFile);

  const [fieldsMap, setFieldsMap] = useState<FieldsMap>(
    mappingArrayToMap(selectedFile.field_mapping),
  );

  const { mapHeadersToFields } = useMapHeadersToFields();
  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [isAutoMapping, setIsAutoMapping] = useState<boolean>(false);

  const startMapFields = async (fileId: string, map: FieldsMap) => {
    setIsStarted(true);
    await mapHeadersToFields(fileId, map);
  };

  const unmappedHeaders =
    selectedFile.headers?.filter((header) => !_.includes(_.values(fieldsMap), header)) || [];

  const disabled = selectedFile.state !== FileState.NEEDS_MAPPING;
  const isMappingLocked = disabled || isAutoMapping;

  const unmappedRequiredFieldCount = requiredFields.filter((field) => {
    // Field is mapped, so it's satisfied
    if (field.fieldId in fieldsMap) {
      return false;
    }

    // Check configured fallback field
    if (field.fallback != null && field.fallback in fieldsMap) {
      return false;
    }

    // Special case: maturity_date_seconds also accepts loan_terms_months as fallback
    // (in addition to the configured remaining_loan_terms_months fallback)
    if (
      field.fieldId === LoanField.maturity_date_seconds &&
      LoanField.loan_terms_months in fieldsMap
    ) {
      return false;
    }

    // Field is not mapped and no fallback is available
    return true;
  }).length;
  const allRequiredMapped = unmappedRequiredFieldCount === 0;

  const onAutoMap = (fieldsMap: FieldsMap) => {
    setFieldsMap(fieldsMap);
  };

  const onMap = (fieldId: string, header: string) => {
    if (isMappingLocked) {
      return;
    }

    setFieldsMap((prevMappings) => ({
      ...prevMappings,
      [fieldId]: header,
    }));
  };

  const onUnmap = (fieldId: LoanField) => {
    if (isMappingLocked) {
      return;
    }

    const updatedMap: FieldsMap = { ...fieldsMap };
    delete updatedMap[fieldId];
    setFieldsMap(updatedMap);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className="rounded border border-accent-muted bg-background-canvas p-5 text-sm"
        data-testid="map-required-fields-card"
      >
        {disabled && <div className="absolute inset-0 z-10 bg-black/50" />}
        <h2 className="mb-2.5 text-lg text-gray-600">Map File Columns to Fields</h2>
        <p className="my-1 text-xs text-gray-500">
          We've recognized some fields that need to be manually mapped to our system. Drag fields
          from your loan tape over to ORSNN defined fields.
        </p>
        <div className="mb-4 grid w-[45%] items-center gap-[6px] rounded bg-pink-900 p-1 text-center text-xl text-pink-100">
          <div className="col-span-1">{selectedFile.name} is selected</div>
          {selectedFile.productType === ProductType.ARM && (
            <div className="col-start-4">{selectedFile.productType}</div>
          )}
        </div>
        <div className="mt-2 flex h-[640px] justify-center gap-8 text-sm">
          <FieldsList
            requiredFields={requiredFields}
            optionalFields={optionalFields}
            map={fieldsMap}
            onMap={onMap}
            onUnmap={onUnmap}
            disabled={isMappingLocked}
          />
          <FileHeadersList unmappedHeaders={unmappedHeaders} disabled={isMappingLocked} />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-end px-3 py-3">
          <LLMAutoMapComponent
            requiredFields={requiredFields}
            optionalFields={optionalFields}
            columns={selectedFile.headers || []}
            fileId={selectedFile.id}
            onAutoMapComplete={onAutoMap}
            onAutoMapLoadingChange={setIsAutoMapping}
          />
          <Button
            data-tooltip-id={`${selectedFile.id}-button-tip`}
            data-testid="map-fields-submit-button"
            disabled={!allRequiredMapped || isMappingLocked}
            onClick={() => selectedFile.id && startMapFields(selectedFile.id, fieldsMap)}
          >
            Map Fields
          </Button>
          {unmappedRequiredFieldCount > 0 && (
            <Tooltip id={`${selectedFile.id}-button-tip`}>
              {unmappedRequiredFieldCount} fields remaining
            </Tooltip>
          )}
        </div>
      </div>
      <MapFieldsOperationWrapper
        fileState={selectedFile.state}
        isStarted={isStarted}
        setIsStarted={setIsStarted}
        processingPercent={selectedFile.processing_percent ?? undefined}
      />
    </DndProvider>
  );
};

export default MapRequiredFieldsCard;
export { MappingCardFragments };
